import { Box } from '@eatclubapp/ec-component-library';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '../../../../components/Button';
import { Spacer } from '../../../../components/Spacer';
import { Typography } from '../../../../components/Typography';
import { updateBookingAction } from '../../../../data/actions/bookingAction';
import {
  fetchObeeAvailabilitySuggestionsAction,
  fetchObeeAvailabilitySuggestionsForAllAreasAction,
} from '../../../../data/actions/obeeAvailabilitySuggestionsAction';
import {
  clearObeeAvailabilitySuggestionsForMultiVenueAction,
  fetchObeeAvailabilitySuggestionsForMultiVenueAction,
} from '../../../../data/actions/obeeMultiVenueAvailabilitySuggestionsAction';
import { bookingKeys, bookingPropTypes, isEditing } from '../../../../data/models/Booking';
import { eventPropTypes } from '../../../../data/models/Event';
import { experiencePropTypes } from '../../../../data/models/Experience';
import { restaurantPropTypes } from '../../../../data/models/Restaurant';
import {
  getAvailabilitySuggestions,
  getAvailabilitySuggestionsForSession,
  obeeAvailabilitySuggestionsPropTypes,
} from '../../../../data/models/obeeAvailabilitySuggestions';
import { obeeBookingPropTypes } from '../../../../data/models/obeeBooking';
import {
  ALL_SESSIONS_LABEL,
  getSessionsForBooking,
  getSessionsForFilter,
} from '../../../../data/models/obeeSessions';
import {
  findObjectByProperty,
  formatIntFromTimeString,
  isEmpty,
  isNil,
  sortByProperty,
  unique,
} from '../../../../utils/helpers';
import { useIsMobile, useIsSmallDevice } from '../../../../utils/hooks';
import { getPreviewSessions, isPreviewMode } from '../../../../utils/previewMode';
import { Carousel } from '../Carousel';
import { JoinWaitlistModal } from './JoinWaitlistModal';
import useStyles from './NextAvailableTimesStyles';
import { Sessions } from './Sessions';
import UnavailabilityCard from './UnavailabilityCard';

const NextAvailableTimes = ({
  booking,
  clearObeeAvailabilitySuggestionsForMultiVenue,
  fetchObeeAvailabilitySuggestions,
  fetchObeeAvailabilitySuggestionsForAllAreas,
  fetchObeeAvailabilitySuggestionsForMultiVenue,
  obeeAvailabilitySuggestions,
  obeeBooking,
  restaurant,
  updateBooking,
  showSessions,
  experiences,
  event,
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const isSmallDevice = useIsSmallDevice(); // for the carousel to account for small mobile devices
  const ref = useRef(null); // For scrolling

  // If an availability is selected on the first load, set it on the booking too
  useEffect(() => {
    if (isEditing(booking)) {
      const selectedAvailability = obeeAvailabilitySuggestions.data.find(
        (availabilitySuggestion) =>
          booking.availabilitySuggestion?.time === availabilitySuggestion.time &&
          booking.availabilitySuggestion?.tables
            ?.map((table) => table?.tableNumber)
            .includes(availabilitySuggestion.tableNumber) &&
          (!booking.availabilitySuggestion?.restId ||
            booking.availabilitySuggestion?.restId === availabilitySuggestion.restId),
      );

      const tableForBooking = findObjectByProperty(
        obeeAvailabilitySuggestions.data.flatMap((suggestion) => suggestion.tables),
        booking?.tableNumber,
        'tableNumber',
      );

      if (!isEmpty(selectedAvailability)) {
        updateBooking(bookingKeys.availabilitySuggestion, {
          restId: booking.initialRestId,
          time: booking.time,
          table: tableForBooking,
          preferredArea: booking?.areaId,
        });
      }
    }
  }, [obeeAvailabilitySuggestions.data]);

  // Scroll into view if using experiences
  useEffect(() => {
    // The preview preselects an experience, so skip the scroll to keep it at the top
    if (!isEmpty(booking?.selectedExperiences) && ref?.current && !isPreviewMode()) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [booking?.selectedExperiences, ref?.current]);

  const sessions = isPreviewMode()
    ? getPreviewSessions(restaurant.data.areas)
    : getSessionsForBooking(restaurant.data.areas, booking).sort((a, b) =>
        sortByProperty(a, b, 'startTime'),
      );

  const sessionsWithUniqueStartTimes = unique(sessions, 'startTime');

  const carouselColumns = (isSmallDevice && 2) || (isMobile && 3) || 4;
  const carouselRows = (isSmallDevice && 4) || (isMobile && 4) || 3;
  const gridSize = carouselColumns * carouselRows;

  const fetchAvailabilitySuggestions = () => {
    const fromEventWidget = event?.data != null;

    if (!booking?.date) {
      return;
    }

    const selectedExperience = booking?.selectedExperiences?.[0]?.experience;
    const areas = selectedExperience?.selectableAreas ?? [];
    const specificArea = areas?.map((a) => a?.areaId);
    const selectableTimes =
      selectedExperience?.selectableTimes?.timeOption === 'exactly'
        ? [formatIntFromTimeString(selectedExperience.selectableTimes?.startTime, 'h:mma')]
        : null;
    const timesPayload =
      selectableTimes ?? sessionsWithUniqueStartTimes.map((session) => session.startTime);

    // HACK: if there is only one area, fetch for specific area
    if (restaurant?.data?.areas?.length === 1 || specificArea?.length === 1) {
      fetchObeeAvailabilitySuggestions(
        specificArea?.length === 1 ? specificArea[0] : restaurant?.data?.areas?.[0]?.id,
        booking.date,
        timesPayload,
        booking.guests,
        booking.selectedExperiences,
        obeeBooking?.data?.id,
        fromEventWidget,
      );
      return;
    }

    if (booking?.preferredAreaId === -1) {
      fetchObeeAvailabilitySuggestionsForAllAreas(
        booking.date,
        timesPayload,
        booking.guests,
        booking.selectedExperiences,
        obeeBooking?.data?.id,
        fromEventWidget,
      );
    } else {
      fetchObeeAvailabilitySuggestions(
        booking?.preferredAreaId,
        booking.date,
        timesPayload,
        booking.guests,
        booking.selectedExperiences,
        obeeBooking?.data?.id,
        fromEventWidget,
      );
    }
  };

  useEffect(() => {
    // clear the multi venue suggestions when the params change and we do another parent fetch
    clearObeeAvailabilitySuggestionsForMultiVenue();

    fetchAvailabilitySuggestions();
  }, [
    fetchObeeAvailabilitySuggestions,
    booking?.preferredAreaId,
    booking.date,
    booking.guests,
    booking.selectedExperiences,
    isEditing(booking), // force a fetch if coming from the edit flow
  ]);

  useEffect(() => {
    if (obeeAvailabilitySuggestions.success) {
      // don't fetch multi venue availability for free accounts (bypassed in preview mode)
      if (!restaurant.data.premium && !isPreviewMode()) {
        return;
      }

      // don't fetch multi venue availability if there are no linked venues (bypassed in preview mode)
      if (!restaurant.data.linkedVenues?.length > 0 && !isPreviewMode()) {
        return;
      }

      // don't fetch multi venue availability for edit flow
      if (isEditing(booking)) {
        return;
      }

      const hasNoAvailability = obeeAvailabilitySuggestions.data.every((availabilitySuggestion) =>
        isNil(availabilitySuggestion.table),
      );

      if (
        (restaurant.data.suggestAvailability === 'no_availability' &&
          (hasNoAvailability || obeeAvailabilitySuggestions.data.length === 0)) ||
        (restaurant.data.suggestAvailability === 'always' && !event?.data) ||
        isPreviewMode()
      ) {
        fetchObeeAvailabilitySuggestionsForMultiVenue(
          booking.date,
          [-1], // note: Sends an array of 1 start time, which is ignored on the backend
          booking.guests,
          gridSize,
        );
      }
    }
  }, [obeeAvailabilitySuggestions.success]);

  useEffect(() => {
    if (booking.preferredTime !== -1) {
      const suggestion = obeeAvailabilitySuggestions.data.find(
        (availabilitySuggestion) =>
          availabilitySuggestion.time === booking.preferredTime &&
          !isNil(availabilitySuggestion.table),
      );

      // if preferred time availability suggestion exists let's set it for them
      if (suggestion) {
        updateBooking(bookingKeys.availabilitySuggestion, {
          ...suggestion,
          allAreas: booking?.preferredAreaId === -1,
          waitlist: false,
        });
      }
    }
  }, [booking.preferredTime, obeeAvailabilitySuggestions.data]);

  // TODO test this doesn't cause problems. What if a user is on the waitlist then returns to the first screen?
  useEffect(() => {
    updateBooking('waitlist', false);
  }, []);

  // Remove sessions that don't match the filtered area or session
  // This prevents it getting confused if there are/aren't sitting times in other sessions
  const sessionsForFilter = getSessionsForFilter(sessions, booking);

  const suggestionsForSession = getAvailabilitySuggestionsForSession(
    booking,
    sessionsForFilter,
    obeeAvailabilitySuggestions.data,
    obeeBooking.data,
    restaurant.data,
    sessions,
    booking.sessionLabel,
  );

  const preferredSuggestionsForSession = suggestionsForSession.filter((suggestionForSession) => {
    if (booking.preferredTime !== -1) {
      return suggestionForSession.time === booking.preferredTime;
    }

    return true;
  });

  const preferredAvailableSuggestionsForSession = preferredSuggestionsForSession.filter(
    (suggestion) => !suggestion.waitlist && !isEmpty(suggestion?.tables),
  );

  const availableSuggestionsForSession = suggestionsForSession.filter(
    (suggestion) => !suggestion.waitlist && !isEmpty(suggestion?.tables),
  );

  // TODO if area changes and the filtered session has no bookings, switch back to All

  // TODO this is very expensive and should be optimized
  const suggestionsBySession = useMemo(() => {
    // Try to find all the sessions that would have bookings. This ends up being a bit weird, since the availabilities can change quite a bit.
    // We need to treat each session as if it was filtered to that session, then run all of the logic of finding bookings for that session.
    return (
      sessions
        .filter((session) => session.id !== 0)
        // Include other sessions such as area id 0
        .map((session) => {
          return {
            mainSession: session,
            linkedSessions: sessions.filter((linkedSession) => {
              return (
                (linkedSession?.areaId <= 0 ||
                  session.areaId <= 0 ||
                  session.areaId === linkedSession?.areaId) &&
                `${session.label}`.toLowerCase() === `${linkedSession?.label}`.toLowerCase()
              );
            }),
          };
        })
        .map((session) => {
          const availabilitySuggestionsForLinkedSessions = getAvailabilitySuggestionsForSession(
            booking,
            session.linkedSessions,
            obeeAvailabilitySuggestions.data,
            obeeBooking.data,
            restaurant.data,
            sessions,
            session.mainSession.label,
          );

          const suggestionsForSpecificSession = getAvailabilitySuggestions(
            availabilitySuggestionsForLinkedSessions,
            booking,
            obeeBooking.data,
            restaurant.data,
            session.mainSession,
          );

          return {
            sessionId: session.mainSession.id,
            availabilitySuggestions: suggestionsForSpecificSession,
          };
        })
    );
  }, [suggestionsForSession, preferredAvailableSuggestionsForSession, sessions]);

  const isDiningOverride = booking?.selectedExperiences?.some(
    (experience) =>
      experience?.experience?.experienceTypeSettings?.dining &&
      !experience?.experience?.experienceTypeSettings?.menu &&
      !experience?.experience?.experienceTypeSettings?.event &&
      !experience?.experience?.experienceTypeSettings?.accommodation,
  );

  const isUnavailableDueToOverride = isDiningOverride && experiences?.forceAccommodationSelection;

  const isUnavailable =
    (!isEmpty(booking?.selectedExperiences) &&
      booking?.selectedExperiences?.[0]?.experience?.available <= 0) ||
    isEmpty(preferredAvailableSuggestionsForSession) ||
    isUnavailableDueToOverride;

  // Clear availabilitySuggestion when selected experience is unavailable.
  // Skipped in preview mode: isUnavailable is briefly true before the mocked
  // suggestions load, which would wipe the preselected time
  useEffect(() => {
    if (isUnavailable && !isPreviewMode()) {
      updateBooking(bookingKeys.availabilitySuggestion, null);
    }
  }, [isUnavailable]);

  const waitlistSetting = event?.data ? event?.data?.hasWaitlist : restaurant.data.waitlist;

  const showWaitlist =
    !obeeAvailabilitySuggestions.pending &&
    !booking.editBooking &&
    (availableSuggestionsForSession.length === 0 || isUnavailable) &&
    waitlistSetting;

  const hasValidWLTime = useMemo(() => {
    if (showWaitlist) {
      const validSessions = getSessionsForFilter(
        getSessionsForBooking(restaurant.data.areas, booking),
        booking,
      );
      // If there is no session match current time, that means venue is closed for the day, and we should not show the waitlist
      return !isEmpty(validSessions);
    }

    return false;
  }, [showWaitlist]);

  const renderSuggestions = () => {
    // Fetching state
    // The "experiences pending" check prevents showing the time selection before receiving the experiences response,
    // and allows moving forward without selecting an experience even if the venue requires an experience selection.
    if (obeeAvailabilitySuggestions.pending || experiences?.pending) {
      return <Carousel columns={carouselColumns} loading rows={1} />;
    }

    if (booking.date && obeeAvailabilitySuggestions.success) {
      // Success with no suggestions
      return (
        <>
          {/* Success with suggestions */}
          {!isUnavailable && preferredAvailableSuggestionsForSession.length > 0 && (
            <>
              <Carousel
                columns={carouselColumns}
                items={preferredAvailableSuggestionsForSession}
                rows={Math.min(
                  Math.ceil(preferredAvailableSuggestionsForSession.length / carouselColumns),
                  carouselRows,
                )}
              />
            </>
          )}

          {/* /!* Show sitting times that have labelled subgroups *!/ */}
          {/* {sittingTimeAvailabilitiesWithTables.map((sittingTime) => ( */}
          {/*   <Spacer direction='vertical' gap={12} key={sittingTime.label}> */}
          {/*     {(booking.sessionLabel === ALL_SESSIONS_LABEL || */}
          {/*       booking.sessionLabel !== sittingTime?.label) && <Box>{sittingTime.label}</Box>} */}
          {/*     <Carousel */}
          {/*       columns={carouselColumns} */}
          {/*       items={sittingTime.availabilities} */}
          {/*       rows={Math.min(sittingTime.availabilities.length / carouselColumns, carouselRows)} */}
          {/*     /> */}
          {/*   </Spacer> */}
          {/* ))} */}

          {/* If no availability was found */}
          {isUnavailable && (
            <UnavailabilityCard fetchAvailabilitySuggestions={fetchAvailabilitySuggestions} />
          )}

          {/* Default suggestions or something? */}
          {!isUnavailable &&
            preferredAvailableSuggestionsForSession.length <= 0 &&
            availableSuggestionsForSession.length >= 0 && (
              <>
                <Carousel
                  columns={carouselColumns}
                  items={availableSuggestionsForSession}
                  rows={Math.min(
                    availableSuggestionsForSession.length / carouselColumns,
                    carouselRows,
                  )}
                />
              </>
            )}
        </>
      );
    }

    // Initial state
    return null;
  };

  const showOrLabel = useMemo(() => {
    return (experiences?.data ?? []).length > 0
      ? experiences?.requireExperienceSelection !== true
      : false;
  }, [experiences]);

  return (
    <Spacer direction='vertical' gap={32} ref={ref}>
      <Spacer direction='vertical' gap={20}>
        <Spacer
          centered
          style={{ justifyContent: 'space-between', minHeight: '33px', paddingTop: '12px' }}
          wrap
        >
          <Spacer direction='vertical'>
            <Typography variant='h4'>
              {showOrLabel && 'Or '} {restaurant?.data?.selectTimeLabel}
            </Typography>
          </Spacer>

          {showSessions && <Sessions suggestionsBySession={suggestionsBySession} />}
        </Spacer>

        <Spacer direction='vertical' gap='m' style={{ width: '100%', minHeight: 'auto' }}>
          {renderSuggestions()}
        </Spacer>
      </Spacer>

      <Spacer style={{ justifyContent: 'space-between', flexDirection: isMobile && 'column' }}>
        {!booking.editBooking && showWaitlist && hasValidWLTime && (
          <Spacer direction='vertical' gap={12}>
            <Box className={classes.joinWaitlistText}>
              <Typography>Can&apos;t find availability?</Typography>
            </Box>
            <Button
              onClick={() => setOpen(true)}
              variant='outlined'
              size='large'
              style={{ minWidth: '144px' }}
              fullWidth={isMobile}
            >
              Join the waitlist
            </Button>
          </Spacer>
        )}
      </Spacer>

      {showWaitlist && <JoinWaitlistModal open={open} close={() => setOpen(false)} />}
    </Spacer>
  );
};

NextAvailableTimes.defaultProps = {
  showSessions: true,
};

NextAvailableTimes.propTypes = {
  booking: bookingPropTypes.isRequired,
  clearObeeAvailabilitySuggestionsForMultiVenue: PropTypes.func.isRequired,
  fetchObeeAvailabilitySuggestions: PropTypes.func.isRequired,
  fetchObeeAvailabilitySuggestionsForAllAreas: PropTypes.func.isRequired,
  fetchObeeAvailabilitySuggestionsForMultiVenue: PropTypes.func.isRequired,
  obeeAvailabilitySuggestions: obeeAvailabilitySuggestionsPropTypes.isRequired,
  obeeBooking: obeeBookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  updateBooking: PropTypes.func.isRequired,
  showSessions: PropTypes.bool,
  experiences: experiencePropTypes.isRequired,
  event: eventPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  obeeAvailabilitySuggestions: state.obeeAvailabilitySuggestions,
  obeeBooking: state.obeeBooking,
  restaurant: state.restaurant,
  experiences: state.experiences,
  event: state.event,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      clearObeeAvailabilitySuggestionsForMultiVenue:
        clearObeeAvailabilitySuggestionsForMultiVenueAction,
      fetchObeeAvailabilitySuggestions: fetchObeeAvailabilitySuggestionsAction,
      fetchObeeAvailabilitySuggestionsForAllAreas:
        fetchObeeAvailabilitySuggestionsForAllAreasAction,
      fetchObeeAvailabilitySuggestionsForMultiVenue:
        fetchObeeAvailabilitySuggestionsForMultiVenueAction,
      updateBooking: updateBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(NextAvailableTimes);
